/**
 * useIosInstallGuide — companion to `useInstallPrompt`.
 *
 * iOS Safari never fires `beforeinstallprompt`, so we can't defer a native
 * dialog. Instead, we detect the case and expose a `showGuide` flag so a
 * UI component can render manual "Tap Share → Add to Home Screen"
 * instructions.
 *
 * Only true when ALL of these hold:
 *   • Device is iOS or iPadOS (covers iPad in desktop-Safari mode too).
 *   • Browser is Safari (Chrome/Edge/Firefox on iOS are WKWebView wrappers
 *     that cannot Add-to-Home-Screen).
 *   • The page is not already running standalone (i.e. not already installed).
 *   • The user hasn't dismissed the guide in the last 30 days.
 *   • They haven't installed the app previously.
 *
 * Shares the same localStorage keys as `useInstallPrompt` so users don't get
 * both prompts.
 */
import { useCallback, useEffect, useState } from "react";
import { trackInstallEvent } from "@/lib/pwaAnalytics";

const DISMISS_KEY = "advoset:pwa:installDismissedAt";
const INSTALLED_KEY = "advoset:pwa:installed";
const SNOOZE_DAYS = 30;

function isStandalone() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  } catch (_) { /* older browsers */ }
  return typeof window.navigator?.standalone === "boolean" && window.navigator.standalone;
}

function isIosDevice() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // iPhone / iPod / iPad (classic)
  if (/iP(hone|ad|od)/.test(ua)) return true;
  // iPadOS 13+ reports as Mac; disambiguate by touch support.
  if (window.navigator.platform === "MacIntel" && (window.navigator.maxTouchPoints || 0) > 1) return true;
  return false;
}

function isIosSafari() {
  if (!isIosDevice()) return false;
  const ua = window.navigator.userAgent || "";
  // Exclude Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS), Opera (OPT/OPiOS).
  return !/CriOS|FxiOS|EdgiOS|OPT|OPiOS|GSA/i.test(ua);
}

function isSnoozed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return (Date.now() - ts) < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
  } catch (_) { return false; }
}

function isInstalled() {
  if (isStandalone()) return true;
  try { return localStorage.getItem(INSTALLED_KEY) === "1"; }
  catch (_) { return false; }
}

export function useIosInstallGuide() {
  const [eligible, setEligible] = useState(false);

  useEffect(() => {
    // Compute once at mount — none of these signals change during a session.
    setEligible(isIosSafari() && !isInstalled() && !isSnoozed());
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); }
    catch (_) { /* ignore */ }
    setEligible(false);
    trackInstallEvent("ios_guide_dismissed", "ios_guide");
  }, []);

  return { showGuide: eligible, dismiss };
}
